import { useEffect } from 'react';
import useApiEndpoints from '../../context/useApiEndpoints';
import useApi from '../useApi';
import { buildQueryParams } from '../endpoints/queryParamsHelper';
import apiEndpointUtils from 'src/utils/apiEndpointUtils';
import { useToast } from 'src/app/useToast';
import { BunkeringEventBM } from 'src/types/BusinessModel/Bunkering/BunkeringEventBM';
import { mapBunkeringBMs, mapBunkeringEventBMs, mapFuelRobBMs, mapBunkerLossBMs } from 'src/factory/Bunkering/BunkeringFactory';
import { useBunkerContext } from 'src/context/BunkerContext';
import { BunkeringBM } from 'src/types/BusinessModel/Bunkering/BunkeringBM';
import { UUID } from 'crypto';
import { FuelRobBM } from 'src/types/BusinessModel/Bunkering/FuelRobBM';
import { BunkeringStatusBM } from 'src/types/BusinessModel/Bunkering/BunkeringStatusBM';
import { useBunkerUpdateHandler } from 'src/app/useBunkerUpdateHandler';
import { useAuthStore } from 'src/context/AuthContext';
import { BunkerLossBM } from 'src/types/BusinessModel/Bunkering/BunkerLossBM';
import { BunkerReceivedBM } from 'src/types/BusinessModel/Bunkering/BunkerReceivedBM';
import { getAllBunkeringItems, getAllBunkeringStatusItems, getAllFuelRobItems } from './offlineBunkeringApi';

export default () => {
    const { apiEndpoints } = useApiEndpoints();
    const { state, getApiData, postApiData } = useApi();
    const { setBunkeringEvents, bunkeringEvents, setBunkerings, setFuelRobData, setBunkerLossData, setDeletedBunkerings } = useBunkerContext();
    const { processApiEndpointError } = apiEndpointUtils();
    const { toastError } = useToast();
    const { handleUpdatedBunkering, handleUpdatedBunkeringStatus, handleDeletedBunkeringStatus, handleUpdatedRobs } = useBunkerUpdateHandler();
    const { signalRConnectionId } = useAuthStore();

    const getBunkeringEvents = async (
        imoNumber: number,
        fromDate: string,
        toDate: string,
        callInBackground: boolean,
        skipUpdatingContext: boolean = false
    ) => {
        const response = getApiData({
            endpoint: apiEndpoints['getBunkeringEvents'].url,
            queryParam: buildQueryParams({ imoNumber, fromDate, toDate }),
            callInBackground: callInBackground,
        });

        if (skipUpdatingContext) {
            const bms = (await response) as BunkeringEventBM[];
            const _fuelRobData = mapBunkeringEventBMs(bms);
            return _fuelRobData;
        }
    };

    const getBunkerings = (imoNumber: number, fromDate: string, toDate: string, callInBackground: boolean) =>
        getApiData({
            endpoint: apiEndpoints['getBunkerings'].url,
            queryParam: buildQueryParams({ imoNumber, fromDate, toDate }),
            callInBackground: callInBackground,
        });

    const upsertBunkering = async (imoNumber: number, bunkering: BunkeringBM) =>
        await postApiData({
            endpoint: apiEndpoints['upsertBunkering'].url,
            data: bunkering,
            queryParam: buildQueryParams({ imoNumber, connectionId: signalRConnectionId }),
            callInBackground: true,
        });

    const getFuelRobData = async (
        imoNumber: number,
        noonReportIds: number[],
        bunkeringGuids: UUID[],
        callInBackground: boolean = false,
        skipUpdatingContext: boolean = false
    ) => {
        const noonReportIdsStr = noonReportIds.join(',');
        const bunkeringGuidsStr = bunkeringGuids.join(',');
        const response = getApiData({
            endpoint: apiEndpoints['getFuelRobData'].url,
            queryParam: buildQueryParams({
                imoNumber,
                noonReportIds: noonReportIdsStr,
                bunkeringGuids: bunkeringGuidsStr,
                connectionId: signalRConnectionId,
            }),
            additionalInfo: { skipUpdatingContext: skipUpdatingContext },
            callInBackground: callInBackground,
        });

        if (skipUpdatingContext) {
            const bms = (await response) as FuelRobBM[];
            const offlineFuelRobs = await getAllFuelRobItems();
            const _fuelRobData = mapFuelRobBMs(bms, offlineFuelRobs);
            return _fuelRobData;
        }
    };

    const upsertFuelRobData = (fuelRobData: FuelRobBM[], imoNumber: number) =>
        postApiData({
            endpoint: apiEndpoints['upsertFuelRobData'].url,
            data: fuelRobData,
            queryParam: buildQueryParams({ imoNumber, connectionId: signalRConnectionId }),
            callInBackground: true,
        });

    const upsertBunkeringStatus = (status: BunkeringStatusBM, imoNumber: number) =>
        postApiData({
            endpoint: apiEndpoints['upsertBunkeringStatus'].url,
            data: status,
            queryParam: buildQueryParams({ imoNumber, connectionId: signalRConnectionId }),
            callInBackground: true,
        });

    const deleteBunkeringStatus = (bunkeringStatusId: number, bunkeringStatusGuid: UUID, imoNumber: number) =>
        getApiData({
            endpoint: apiEndpoints['deleteBunkeringStatus'].url,
            queryParam: buildQueryParams({ imoNumber, connectionId: signalRConnectionId, bunkeringStatusId, bunkeringStatusGuid }),
            additionalInfo: { bunkeringStatusId, bunkeringStatusGuid },
        });

    const getBunkerLossData = (noonReportIds: number[], imoNumber: number) =>
        getApiData({
            endpoint: apiEndpoints['getBunkerLossData'].url,
            queryParam: buildQueryParams({ imoNumber, noonReportIds }),
        });

    const upsertBunkerLossData = (bunkerLossData: BunkerLossBM[], imoNumber: number, noonReportId: number) =>
        postApiData({
            endpoint: apiEndpoints['upsertBunkerLossData'].url,
            data: bunkerLossData,
            queryParam: buildQueryParams({ imoNumber, connectionId: signalRConnectionId, noonReportId }),
            callInBackground: true,
        });

    const upsertDebunkeringData = (debunkeringData: BunkerReceivedBM[], imoNumber: number) =>
        postApiData({
            endpoint: apiEndpoints['upsertDebunkeringData'].url,
            data: debunkeringData,
            queryParam: buildQueryParams({ imoNumber, connectionId: signalRConnectionId }),
            callInBackground: true,
        });

    useEffect(() => {
        (async () => {
            if (state.status === 'success') {
                if (state.endpoint === apiEndpoints['getBunkeringEvents'].url) {
                    const bms = state.result as BunkeringEventBM[];
                    const _bunkeringEvents = mapBunkeringEventBMs(bms);
                    setBunkeringEvents(_bunkeringEvents);
                } else if (state.endpoint === apiEndpoints['getBunkerings'].url) {
                    const bms = state.result as BunkeringBM[];
                    const offlineBunkerings = await getAllBunkeringItems();
                    const offlineBunkeringStatuses = await getAllBunkeringStatusItems();
                    const _bunkerings = mapBunkeringBMs(bms, bunkeringEvents, offlineBunkerings, offlineBunkeringStatuses);
                    setBunkerings(_bunkerings.bunkerings);
                    setDeletedBunkerings(_bunkerings.deletedBunkerings);
                } else if (state.endpoint === apiEndpoints['upsertBunkering'].url) {
                    const bm = state.result as BunkeringBM;
                    handleUpdatedBunkering(bm);
                } else if (state.endpoint === apiEndpoints['getFuelRobData'].url) {
                    if (state.additionalInfo?.skipUpdatingContext) return;
                    const bms = state.result as FuelRobBM[];
                    const offlineFuelRobs = await getAllFuelRobItems();

                    const _fuelRobData = mapFuelRobBMs(bms, offlineFuelRobs);
                    setFuelRobData(_fuelRobData);
                } else if (state.endpoint === apiEndpoints['upsertFuelRobData'].url) {
                    const bms = state.result as FuelRobBM[];
                    const offlineFuelRobs = await getAllFuelRobItems();

                    handleUpdatedRobs(bms, offlineFuelRobs);
                } else if (state.endpoint === apiEndpoints['upsertBunkeringStatus'].url) {
                    const bm = state.result as BunkeringStatusBM;
                    handleUpdatedBunkeringStatus(bm);
                } else if (state.endpoint === apiEndpoints['deleteBunkeringStatus'].url) {
                    const statusGuid = state.additionalInfo.bunkeringStatusGuid;
                    handleDeletedBunkeringStatus(statusGuid);
                } else if (state.endpoint === apiEndpoints['getBunkerLossData'].url) {
                    const bms = state.result as BunkerLossBM[];
                    const _bunkerLossData = mapBunkerLossBMs(bms);
                    setBunkerLossData(_bunkerLossData);
                } else if (state.endpoint === apiEndpoints['upsertBunkerLossData'].url) {
                    const bms = state.result as BunkerLossBM[];
                    const _bunkerLossData = mapBunkerLossBMs(bms);
                    setBunkerLossData(_bunkerLossData);
                } else if (state.endpoint === apiEndpoints['upsertDebunkeringData'].url) {
                    const bm = state.result as BunkeringBM;
                    handleUpdatedBunkering(bm);
                }
            } else {
                if (state.endpoint === apiEndpoints['upsertBunkering'].url && state.error?.response?.status === 409) {
                    toastError(
                        'Cannot delete this BDN: consumption records exist for this bunkering. Remove consumption entries before deleting.',
                        8000
                    );
                } else {
                    processApiEndpointError(state);
                }
            }
        })();
    }, [state]);

    return {
        state,
        getBunkeringEvents,
        getBunkerings,
        upsertBunkering,
        getFuelRobData,
        upsertFuelRobData,
        upsertBunkeringStatus,
        deleteBunkeringStatus,
        getBunkerLossData,
        upsertBunkerLossData,
        upsertDebunkeringData,
    };
};
